import { useState, ChangeEvent, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { showSuccess, showError } from "@/utils/toast";
import { Upload, Trash2, Plus, Calendar } from "lucide-react";

interface Country {
  id: number;
  code: string;
  translations: { [key: string]: string[] };
}

interface Shop {
  id: number;
  name: string;
  country_code: string;
  image_url: string | null;
}

interface Library {
  id: number;
  name: string;
  children?: Library[];
  children_count?: number;
  total_images_count?: number;
}

interface DateRange {
  id: number;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

interface ImageUploaderProps {
  onUploadSuccess?: () => void;
}

const ImageUploader = ({ onUploadSuccess }: ImageUploaderProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | "">();
  const [selectedShop, setSelectedShop] = useState<number | "">();
  const [selectedLibrary, setSelectedLibrary] = useState<number | "">();
  const [dateRanges, setDateRanges] = useState<DateRange[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});

  // Országok betöltése
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch("/api/app/countries");
        if (response.ok) {
          const data = await response.json();
          setCountries(data.countries || []);
        } else {
          showError("Nem sikerült az országok betöltése.");
        }
      } catch (error) {
        console.error("Hiba az országok betöltésekor:", error);
        showError("Hiba az országok betöltésekor.");
      }
    };

    fetchCountries();
  }, []);

  const flattenLibraries = (libs: Library[], depth: number = 0): Library[] => {
    let result: Library[] = [];
    for (const lib of libs) {
      const prefix = depth > 0 ? "  ".repeat(depth) + "└─ " : "";
      result.push({
        ...lib,
        name: prefix + lib.name
      });
      if (lib.children && lib.children.length > 0) {
        result = [...result, ...flattenLibraries(lib.children, depth + 1)];
      }
    }
    return result;
  };

  // Könyvtárak betöltése
  useEffect(() => {
    const fetchLibraries = async () => {
      try {
        const response = await fetch("/api/admin/libraries");
        if (response.ok) {
          const data = await response.json();
          const flatLibraries = flattenLibraries(data.libraries || []);
          setLibraries(flatLibraries);
        } else {
          showError("Nem sikerült a könyvtárak betöltése.");
        }
      } catch (error) {
        console.error("Hiba a könyvtárak betöltésekor:", error);
        showError("Hiba a könyvtárak betöltésekor.");
      }
    };

    fetchLibraries();
  }, []);

  // Boltok betöltése ország kiválasztása után
  useEffect(() => {
    if (!selectedCountry) {
      setShops([]);
      setSelectedShop("");
      return;
    }

    const fetchShops = async () => {
      try {
        const response = await fetch(`/api/app/shops?country_code=${selectedCountry}`);
        if (response.ok) {
          const data = await response.json();
          setShops(data.shops || []);
          setSelectedShop(""); // Alaphelyzetbe állítja a bolt kiválasztást
        } else {
          showError("Nem sikerült a boltok betöltése.");
          setShops([]);
        }
      } catch (error) {
        console.error("Hiba a boltok betöltésekor:", error);
        showError("Hiba a boltok betöltésekor.");
        setShops([]);
      }
    };

    fetchShops();
  }, [selectedCountry]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];

      const validFiles = newFiles.filter(file => {
        if (!validImageTypes.includes(file.type)) {
          showError(`"${file.name}" nem támogatott képformátum.`);
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        setSelectedFiles([...selectedFiles, ...validFiles]);
      }

      event.target.value = "";
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const addDateRange = () => {
    const today = new Date().toISOString().split('T')[0];
    const newDateRange: DateRange = {
      id: Date.now(),
      startDate: today,
      endDate: today,
    };
    setDateRanges([...dateRanges, newDateRange]);
  };

  const removeDateRange = (id: number) => {
    setDateRanges(dateRanges.filter((range) => range.id !== id));
  };

  const updateDateRange = (
    id: number,
    field: 'startDate' | 'endDate',
    value: string
  ) => {
    setDateRanges(
      dateRanges.map((range) => {
        if (range.id === id) {
          const updated = { ...range, [field]: value };
          // Ellenőrzés: ha a kezdés után van a vége
          if (field === 'startDate' && updated.endDate < value) {
            updated.endDate = value;
          }
          if (field === 'endDate' && updated.startDate > value) {
            updated.startDate = value;
          }
          return updated;
        }
        return range;
      })
    );
  };

  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split('-');
    return `${year}. ${month}. ${day}.`;
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showError("Nincs fájl kiválasztva a feltöltéshez.");
      return;
    }

    if (!selectedCountry) {
      showError("Kérlek, válassz ki egy országot.");
      return;
    }

    if (!selectedShop) {
      showError("Kérlek, válassz ki egy boltot.");
      return;
    }

    if (!selectedLibrary) {
      showError("Kérlek, válassz ki egy könyvtárat.");
      return;
    }

    setLoading(true);
    const totalFiles = selectedFiles.length;
    let successCount = 0;
    let failureCount = 0;

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        try {
          const formData = new FormData();
          formData.append("image", file);
          formData.append("country_id", selectedCountry.toString());
          formData.append("shop_id", selectedShop.toString());
          formData.append("library_id", selectedLibrary.toString());

          if (dateRanges.length > 0) {
            formData.append("date_ranges", JSON.stringify(dateRanges));
          }

          const response = await fetch("/api/admin/upload-image", {
            method: "POST",
            body: formData,
          });

          if (response.ok) {
            successCount++;
            setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
          } else {
            failureCount++;
            const error = await response.json();
            console.error(`Hiba: ${file.name}:`, error.message);
          }
        } catch (error) {
          failureCount++;
          console.error(`Hiba a feltöltés során: ${file.name}`, error);
        }

        setUploadProgress(prev => ({
          ...prev,
          [file.name]: ((i + 1) / totalFiles) * 100
        }));
      }

      if (successCount > 0) {
        showSuccess(`${successCount} fájl sikeresen feltöltve!`);
      }
      if (failureCount > 0) {
        showError(`${failureCount} fájl feltöltése sikertelen.`);
      }

      setSelectedFiles([]);
      setSelectedCountry("");
      setSelectedShop("");
      setDateRanges([]);
      setUploadProgress({});

      const fileInput = document.getElementById("image-files") as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }

      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Hiba a feltöltés során:", error);
      showError("Hiba a feltöltés során.");
    } finally {
      setLoading(false);
    }
  };

  const getCountryName = (country: Country) => {
    return country.translations.HU?.[0] || country.code;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Képek Feltöltése</CardTitle>
        <CardDescription>
          Válassz ki több képfájlt, egy országot és egy boltot, majd töltsd fel őket egyszerre.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Képfájlok kiválasztás */}
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="image-files">Képfájlok (többszörös kiválasztás)</Label>
          <Input
            id="image-files"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
          />
          {selectedFiles.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-sm font-semibold">
                Kiválasztott fájlok: {selectedFiles.length} db
              </p>
              <div className="max-h-64 overflow-y-auto border rounded-md p-3 bg-slate-50 dark:bg-slate-900">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between py-2 px-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                  >
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    {uploadProgress[file.name] === 100 && (
                      <span className="text-xs text-green-600 font-semibold mr-2">✓</span>
                    )}
                    {uploadProgress[file.name] !== undefined && uploadProgress[file.name] < 100 && (
                      <div className="w-16 h-1 bg-gray-300 rounded-full mr-2 overflow-hidden">
                        <div
                          className="h-full bg-blue-500"
                          style={{ width: `${uploadProgress[file.name]}%` }}
                        ></div>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Ország kiválasztás */}
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="country-select">Ország</Label>
          <select
            id="country-select"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value ? parseInt(e.target.value) : "")}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">-- Válassz egy országot --</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {getCountryName(country)} ({country.code})
              </option>
            ))}
          </select>
        </div>

        {/* Bolt kiválasztás (csak ha van kiválasztott ország) */}
        {selectedCountry && (
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <Label htmlFor="shop-select">Bolt</Label>
            <select
              id="shop-select"
              value={selectedShop}
              onChange={(e) => setSelectedShop(e.target.value === "" ? "" : Number(e.target.value))}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">-- Válassz egy boltot --</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Könyvtár kiválasztás */}
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="library-select">Könyvtár</Label>
          <select
            id="library-select"
            value={selectedLibrary}
            onChange={(e) => setSelectedLibrary(e.target.value ? parseInt(e.target.value) : "")}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">-- Válassz egy könyvtárat --</option>
            {libraries.map((library) => (
              <option key={library.id} value={library.id}>
                {library.name}
              </option>
            ))}
          </select>
        </div>

        {/* Dátum tartományok */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Akció időtartama (Opcionális)</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDateRange}
            >
              <Plus className="mr-2 h-4 w-4" />
              Időtartam hozzáadása
            </Button>
          </div>

          {dateRanges.map((range, index) => (
            <div
              key={range.id}
              className="border rounded-lg p-4 space-y-4 bg-slate-900 dark:bg-slate-800"
            >
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">
                  Időtartam #{index + 1}: {formatDateForDisplay(range.startDate)} - {formatDateForDisplay(range.endDate)}
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDateRange(range.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`start-date-${range.id}`} className="text-xs">
                    <Calendar className="inline h-3 w-3 mr-1 text-white" />
                    Kezdés dátuma
                  </Label>
                  <Input
                    id={`start-date-${range.id}`}
                    type="date"
                    value={range.startDate}
                    onChange={(e) =>
                      updateDateRange(range.id, "startDate", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`end-date-${range.id}`} className="text-xs">
                    <Calendar className="inline h-3 w-3 mr-1 text-white" />
                    Vége dátuma
                  </Label>
                  <Input
                    id={`end-date-${range.id}`}
                    type="date"
                    value={range.endDate}
                    min={range.startDate}
                    onChange={(e) =>
                      updateDateRange(range.id, "endDate", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Feltöltés gomb */}
        <Button
          onClick={handleUpload}
          disabled={selectedFiles.length === 0 || !selectedCountry || !selectedShop || !selectedLibrary || loading}
          className="w-full"
        >
          <Upload className="mr-2 h-4 w-4" />
          {loading ? `Feltöltés folyamatban... (${selectedFiles.length} fájl)` : `Feltöltés (${selectedFiles.length} fájl)`}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ImageUploader;